import { NetworkStatus, gql, useQuery } from '@apollo/client';
import {
  Box,
  Divider,
  IconButton,
  Image,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from '@chakra-ui/react';
import { convertFromRaw } from 'draft-js';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import * as React from 'react';

import { InfoCard } from '@/components/InfoCard';
import { OrganizationProfileImage } from '@/components/OrganizationProfileImage';
import { Redraft } from '@/components/Redraft';
import { Resources } from '@/components/Resources';
import { usePermissions } from '@/hooks/usePermissions';
import { Card } from '@/imports/ui/chakra/components/Card';
import { ImageFallback } from '@/imports/ui/chakra/components/ImageFallback';
import { Loading } from '@/imports/ui/chakra/components/Loading';
import { Prose } from '@/imports/ui/chakra/components/Prose';
import { UnexpectedError } from '@/imports/ui/chakra/components/UnexpectedError';
import { EditIcon } from '@/imports/ui/chakra/feather';
import {
  Strawberry,
  StrawberryAside,
  StrawberryMain,
} from '@/layouts/Strawberry';

import {
  MemberCompany_Organization,
  MemberCompany_OrganizationVariables,
} from './graphql-types/MemberCompany_Organization';

import { CompanyRoster } from '../CompanyRoster';

const ORGANIZATION = gql`
  query MemberCompany_Organization(
    $singleOrganizationInput: SingleOrganizationInput!
    $isAuthenticated: Boolean!
  ) {
    organization(input: $singleOrganizationInput) {
      result {
        _id
        currentUserOrganizationPermission {
          canManageOrganizationProfile
        }
        displayName
        bannerImage {
          _id
          imageProxy(input: { ar: "8:3", w: 1600, fit: "fill", fill: "blur" })
        }
        rosterV2(input: { first: 1 }) @include(if: $isAuthenticated) {
          edges {
            node {
              _id
            }
          }
        }
        aboutUs
        ...OrganizationProfileImage_OrganizationFragment
        ...OrganizationPageInformation_OrganizationFragment
      }
    }
  }
  ${OrganizationProfileImage.organizationFragment}
  ${InfoCard.organizationFragment}
`;

export const MemberCompany: React.FC = () => {
  const router = useRouter();

  const id = router.query.organizationId.toString();

  const permissions = usePermissions();

  const query = useQuery<
    MemberCompany_Organization,
    MemberCompany_OrganizationVariables
  >(ORGANIZATION, {
    variables: {
      singleOrganizationInput: {
        id,
      },
      isAuthenticated: permissions.isAuthenticated,
    },
    notifyOnNetworkStatusChange: true,
  });

  const organization = query.data?.organization?.result;

  const hasAboutUs =
    organization &&
    organization.aboutUs &&
    convertFromRaw(organization.aboutUs).hasText();

  // careful, typescript doesn't know that `rosterV2` is actually nullable because of graphql include directive
  const hasPeople =
    organization &&
    organization.rosterV2 &&
    organization.rosterV2.edges.length > 0;

  const isLoading = query.networkStatus === NetworkStatus.loading;

  return (
    <Strawberry>
      <StrawberryMain>
        {isLoading ? (
          <Loading />
        ) : query.error ? (
          <UnexpectedError />
        ) : !organization ? (
          <UnexpectedError />
        ) : (
          <Tabs>
            <Stack spacing="4">
              <Card position="relative">
                {organization.bannerImage?.imageProxy ? (
                  <Image
                    alt="company banner"
                    borderTopRadius="xl"
                    src={organization.bannerImage.imageProxy}
                  />
                ) : (
                  <ImageFallback borderTopRadius="xl" aspectRatio="8/3" />
                )}

                {organization.currentUserOrganizationPermission
                  .canManageOrganizationProfile && (
                  <Box position="absolute" top="4" right="4">
                    <NextLink href={`/my-company/dashboard/overview`}>
                      <IconButton
                        icon={<EditIcon />}
                        aria-label="edit profile"
                        variant="outline"
                        colorScheme="gray"
                        size="sm"
                      />
                    </NextLink>
                  </Box>
                )}

                <Stack spacing="0" divider={<Divider />}>
                  <Stack
                    direction={{ base: 'column', md: 'row' }}
                    alignItems={{ base: 'center', md: 'flex-start' }}
                    spacing={{ base: 2, md: 4 }}
                    p="4"
                  >
                    <OrganizationProfileImage
                      mt={{ base: -16, md: -10 }}
                      h={{ base: 28, md: 36 }}
                      w={{ base: 28, md: 36 }}
                      organization={organization}
                    />
                    <Text
                      textStyle="xlBodyStrong"
                      textAlign={{ base: 'center', md: 'left' }}
                    >
                      {organization.displayName}
                    </Text>
                  </Stack>
                  {hasAboutUs && hasPeople && (
                    <TabList px="4">
                      <Tab>Overview</Tab>
                      <Tab>People</Tab>
                    </TabList>
                  )}
                </Stack>
              </Card>
              <TabPanels>
                {hasAboutUs && (
                  <TabPanel>
                    <Card as={Stack} spacing="4" p="6">
                      <Text textStyle="xlBodyStrong">About Us</Text>
                      <Prose>
                        <Redraft rawDraftContentState={organization.aboutUs} />
                      </Prose>
                      <Resources
                        organizationId={organization._id}
                        alt={organization.displayName}
                      />
                    </Card>
                  </TabPanel>
                )}
                {hasPeople && (
                  <TabPanel>
                    <CompanyRoster organizationId={organization._id} />
                  </TabPanel>
                )}
              </TabPanels>
            </Stack>
          </Tabs>
        )}
      </StrawberryMain>
      <StrawberryAside>
        {organization && <InfoCard organization={organization} />}
      </StrawberryAside>
    </Strawberry>
  );
};
